import React, { useState } from "react";
import {
  Modal, Form, Input, Button, message, Divider,
  Descriptions, Tag, Timeline, Upload, Space, Card, Select,
} from "antd";
import {
  PaperClipOutlined, PlusOutlined, CheckCircleOutlined,
  ClockCircleOutlined, UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { Api_Url } from "../../../config/config";

const { TextArea } = Input;
const { Option } = Select;

const getToken = () =>
  JSON.parse(sessionStorage.getItem("user"))?.accessToken;

const authHeader = () => ({
  Authorization: `Bearer ${getToken()}`,
});

// Upload a file to the backend and get back a URL
const uploadAttachment = async (file) => {
  const formData = new FormData();
  formData.append("File", file);
  const res = await axios.post(
    `${Api_Url}api/v1/EmployeeGrievance/upload-attachment`,
    formData,
    {
      headers: {
        ...authHeader(),
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return res?.data?.data?.url || null;
};

export const EmployeeGrievanceModalDetail = ({ record, onClose, onSuccess }) => {
  const [actionForm] = Form.useForm();
  const [closeForm] = Form.useForm();
  const [addingAction, setAddingAction] = useState(false);
  const [closingGrievance, setClosingGrievance] = useState(false);
  const [actionFileList, setActionFileList] = useState([]);
  const [closureFileList, setClosureFileList] = useState([]);
  const [showActionForm, setShowActionForm] = useState(false);
  const [showCloseForm, setShowCloseForm] = useState(false);

  const isClosed = !!record.closureDate;
  const actionCount = record.actions?.length || 0;
  const canAddAction = !isClosed;

  // ── Add Action ────────────────────────────────────────────────────────────
  const handleAddAction = async (values) => {
    setAddingAction(true);
    try {
      let attachmentUrl = null;
      if (actionFileList.length > 0) {
        attachmentUrl = await uploadAttachment(actionFileList[0]);
      }

      const payload = {
        HrRemarks: values.HrRemarks,
        // ActionDate is system-generated on the server
        AttachmentUrl: attachmentUrl,
      };

      const res = await fetch(
        `${Api_Url}api/v1/EmployeeGrievance/${record.id}/actions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeader(),
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();
      if (res.ok) {
        message.success(`Action ${actionCount + 1} added successfully`);
        actionForm.resetFields();
        setActionFileList([]);
        setShowActionForm(false);
        onSuccess();
      } else {
        throw new Error(result.message || "Failed to add action");
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setAddingAction(false);
    }
  };

  // ── Close Grievance ───────────────────────────────────────────────────────
  const handleClose = async (values) => {
    setClosingGrievance(true);
    try {
      let closureAttachmentUrl = null;
      if (closureFileList.length > 0) {
        closureAttachmentUrl = await uploadAttachment(closureFileList[0]);
      }

      const payload = {
        FinalClosureStatus: values.FinalClosureStatus,
        // ClosureDate is system-generated on the server
        ClosureRemarks: values.ClosureRemarks || null,
        ClosureAttachmentUrl: closureAttachmentUrl,
      };

      const res = await fetch(
        `${Api_Url}api/v1/EmployeeGrievance/${record.id}/close`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeader(),
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();
      if (res.ok) {
        message.success("Grievance closed successfully");
        onSuccess();
      } else {
        throw new Error(result.message || "Failed to close grievance");
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setClosingGrievance(false);
    }
  };

  // ── File upload props (prevent auto-upload) ───────────────────────────────
  const uploadProps = (fileList, setFileList) => ({
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      // File type validation
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'text/plain'
      ];
      
      const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.txt'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        message.error('Only PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, BMP, and TXT files are allowed!');
        return Upload.LIST_IGNORE;
      }
      
      // File size validation (10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        message.error('File size must be smaller than 10MB!');
        return Upload.LIST_IGNORE;
      }
      
      setFileList([file]);
      return false;
    },
    fileList,
    maxCount: 1,
    accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.txt',
  });

  return (
    <Modal
      title={
        <Space>
          <span style={{ fontSize: 18, fontWeight: "bold" }}>
            Grievance #{record.id}
          </span>
          <Tag color={isClosed ? "green" : "gold"}>
            {isClosed ? "CLOSED" : "OPEN"}
          </Tag>
          <Tag color={record.type === "Complaint" ? "red" : "orange"}>
            {record.type}
          </Tag>
        </Space>
      }
      open={true}
      onCancel={onClose}
      width={780}
      centered
      styles={{ body: { maxHeight: "75vh", overflowY: "auto", paddingRight: 8 } }}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      {/* ── Basic Details ── */}
      <Descriptions bordered size="small" column={2} className="mb-3">
        <Descriptions.Item label="Employee Name">
          {record.employeeName}
        </Descriptions.Item>
        <Descriptions.Item label="Employee ID">
          {record.employeeCode}
        </Descriptions.Item>
        <Descriptions.Item label="Receipt Date">
          {moment(record.receiptDate).format("DD-MM-YYYY")}
        </Descriptions.Item>
        <Descriptions.Item label="Created By">
          {record.createdBy || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Receipt Detail" span={2}>
          {record.receiptDetails}
        </Descriptions.Item>

        {/* Initial HR Remarks block */}
        {record.hrRemarks && (
          <>
            <Descriptions.Item label="HR Remarks" span={2}>
              {record.hrRemarks}
            </Descriptions.Item>
            {record.attachmentUrl && (
              <Descriptions.Item label="Attachment" span={2}>
                <a href={record.attachmentUrl} target="_blank" rel="noreferrer">
                  <PaperClipOutlined /> View Attachment
                </a>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Date" span={2}>
              <span style={{ color: "#888", fontSize: 12 }}>
                {record.created
                  ? moment(record.created).format("DD-MM-YYYY hh:mm A")
                  : "—"}{" "}
                <Tag style={{ fontSize: 10 }}>System Generated</Tag>
              </span>
            </Descriptions.Item>
          </>
        )}
      </Descriptions>

      {/* ── Action Trail ── */}
      <Divider orientation="left">
        <strong>Trail ({actionCount} actions)</strong>
      </Divider>

      {actionCount === 0 ? (
        <p className="text-muted">No actions recorded yet.</p>
      ) : (
        <Timeline
          items={record.actions.map((a) => ({
            dot: <ClockCircleOutlined style={{ color: "#1677ff" }} />,
            children: (
              <Card
                size="small"
                style={{ marginBottom: 8 }}
                title={
                  <Space>
                    <Tag color="blue">Action {a.actionNumber}</Tag>
                    {a.createdBy && (
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        by {a.createdBy}
                      </span>
                    )}
                  </Space>
                }
              >
                {/* HR Remarks */}
                <p style={{ marginBottom: 6 }}>
                  <strong>HR Remarks:</strong> {a.hrRemarks}
                </p>

                {/* Attachment */}
                {a.attachmentUrl && (
                  <p style={{ marginBottom: 6 }}>
                    <strong>Attachment:</strong>{" "}
                    <a href={a.attachmentUrl} target="_blank" rel="noreferrer">
                      <PaperClipOutlined /> View Attachment
                    </a>
                  </p>
                )}

                {/* System-generated date */}
                <p style={{ marginBottom: 0, color: "#888", fontSize: 12 }}>
                  <strong>Date:</strong>{" "}
                  {a.created
                    ? moment(a.created).format("DD-MM-YYYY hh:mm A")
                    : moment(a.actionDate).format("DD-MM-YYYY")}{" "}
                  <Tag style={{ fontSize: 10 }}>System Generated</Tag>
                </p>
              </Card>
            ),
          }))}
        />
      )}

      {/* ── Add Action Form ── */}
      {canAddAction && (
        <>
          {!showActionForm ? (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              block
              onClick={() => setShowActionForm(true)}
              style={{ marginBottom: 16 }}
            >
              Add Action {actionCount + 1}
            </Button>
          ) : (
            <Card
              size="small"
              title={<strong>Add Action {actionCount + 1}</strong>}
              style={{ marginBottom: 16 }}
              extra={
                <Button size="small" onClick={() => setShowActionForm(false)}>
                  Cancel
                </Button>
              }
            >
              <Form form={actionForm} layout="vertical" onFinish={handleAddAction}>
                <Form.Item
                  name="HrRemarks"
                  label="HR Remarks"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <TextArea rows={3} placeholder="Enter HR remarks..." />
                </Form.Item>

                <Form.Item label="Attachment (Optional)">
                  <Upload {...uploadProps(actionFileList, setActionFileList)}>
                    <Button icon={<UploadOutlined />}>Select File</Button>
                  </Upload>
                </Form.Item>

                {/* Date is system-generated — shown as info only */}
                <Form.Item label="Date">
                  <Input
                    value={new Date().toLocaleDateString("en-GB")}
                    disabled
                    style={{ color: "#888", background: "#f5f5f5" }}
                  />
                  <small style={{ color: "#aaa" }}>System generated</small>
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={addingAction}
                  block
                >
                  Save Action {actionCount + 1}
                </Button>
              </Form>
            </Card>
          )}
        </>
      )}

      {/* ── Closure Section ── */}
      {isClosed ? (
        <>
          <Divider orientation="left">
            <strong>Closure Details</strong>
          </Divider>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Final Status">
              <Tag color="green">{record.finalClosureStatus}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Closure Date">
              {moment(record.closureDate).format("DD-MM-YYYY")}
            </Descriptions.Item>
            <Descriptions.Item label="Closure Remarks" span={2}>
              {record.closureRemarks || "—"}
            </Descriptions.Item>
            {record.closureAttachmentUrl && (
              <Descriptions.Item label="Attachment" span={2}>
                <a
                  href={record.closureAttachmentUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <PaperClipOutlined /> View Closure Attachment
                </a>
              </Descriptions.Item>
            )}
          </Descriptions>
        </>
      ) : (
        <>
          <Divider orientation="left">
            <strong>Close Grievance</strong>
          </Divider>

          {!showCloseForm ? (
            <Button
              danger
              icon={<CheckCircleOutlined />}
              block
              onClick={() => setShowCloseForm(true)}
            >
              Mark as Closed
            </Button>
          ) : (
            <Card
              size="small"
              title={<strong>Closure Details</strong>}
              extra={
                <Button size="small" onClick={() => setShowCloseForm(false)}>
                  Cancel
                </Button>
              }
            >
              <Form form={closeForm} layout="vertical" onFinish={handleClose}>
                <Form.Item
                  name="FinalClosureStatus"
                  label="Final Closure Status"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Select placeholder="Select closure status">
                    <Option value="Resolved">Resolved</Option>
                    <Option value="Withdrawn">Withdrawn</Option>
                    <Option value="Rejected">Rejected</Option>
                    <Option value="Settled">Settled</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="ClosureRemarks" label="Closure Remarks">
                  <TextArea rows={3} placeholder="Final closing notes..." />
                </Form.Item>
                <Form.Item label="Closure Attachment (Optional)">
                  <Upload {...uploadProps(closureFileList, setClosureFileList)}>
                    <Button icon={<UploadOutlined />}>Select File</Button>
                  </Upload>
                </Form.Item>
                <Button
                  type="primary"
                  danger
                  htmlType="submit"
                  loading={closingGrievance}
                  block
                >
                  Confirm Closure
                </Button>
              </Form>
            </Card>
          )}
        </>
      )}
    </Modal>
  );
};
